import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, RefreshCw, Archive, ArchiveRestore } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";

export default function KundenStatusPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | low | pending | ended

  const [archivedList, setArchivedList] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients/status-overview");
      setClients(r.data);
      setArchivedList(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const [archivedCount, setArchivedCount] = useState(0);
  useEffect(() => { api.get("/clients/archive").then((r) => setArchivedCount(r.data.clients.length)).catch(() => {}); }, [clients]);
  useEffect(() => {
    if (filter === "archived" && archivedList === null)
      api.get("/clients/status-overview", { params: { archived: true } }).then((r) => setArchivedList(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [filter, archivedList]);

  const today = new Date().toISOString().slice(0, 10);
  const setArchived = async (c, archived) => {
    if (archived && !window.confirm(`„${c.name}“ archivieren? Der Kunde verschwindet aus der Übersicht, die Akte bleibt im Archiv erreichbar.`)) return;
    try { await api.put(`/clients/${c.client_id}/archived`, { archived }); toast.success(archived ? "Kunde archiviert" : "Kunde wiederhergestellt"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const filtered = useMemo(() => {
    if (filter === "archived") return archivedList || [];
    if (filter === "upcoming") return clients.filter((c) => c.start_date && c.start_date > today);
    if (filter === "running") return clients.filter((c) => c.start_date && c.start_date <= today && (!c.end_date || c.end_date >= today));
    if (filter === "billing") return clients.filter((c) => c.unbilled_ue > 0);
    if (filter === "docs") return clients.filter((c) => c.docs_completeness < 100);
    if (filter === "done") return clients.filter((c) => c.end_date && c.end_date < today && c.unbilled_ue <= 0 && c.pending_signatures === 0);
    if (filter === "low") return clients.filter((c) => c.total_ue > 0 && c.remaining_ue <= 10);
    if (filter === "pending") return clients.filter((c) => c.pending_signatures > 0);
    if (filter === "ended") return clients.filter((c) => c.end_date && c.end_date < today);
    return clients;
  }, [clients, archivedList, filter, today]);

  const stats = useMemo(() => ({
    total: clients.length,
    low: clients.filter((c) => c.total_ue > 0 && c.remaining_ue <= 10).length,
    pending: clients.filter((c) => c.pending_signatures > 0).length,
    ended: clients.filter((c) => c.end_date && c.end_date < today).length,
    upcoming: clients.filter((c) => c.start_date && c.start_date > today).length,
    running: clients.filter((c) => c.start_date && c.start_date <= today && (!c.end_date || c.end_date >= today)).length,
    billing: clients.filter((c) => c.unbilled_ue > 0).length,
    docs: clients.filter((c) => c.docs_completeness < 100).length,
    done: clients.filter((c) => c.end_date && c.end_date < today && c.unbilled_ue <= 0 && c.pending_signatures === 0).length,
  }), [clients, today]);
  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="kunden-status-root">
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">AVGS · Admin</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            Kunden-Statusblick
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Alle Kunden auf einen Blick — sortiert nach verbleibenden UE. Wer hat wenig übrig? Wer wartet auf Unterschriften? Wer läuft demnächst aus?
          </p>
        </div>
        <button
          data-testid="reload-btn"
          onClick={load}
          className="px-4 py-3 border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm flex items-center gap-2"
        >
          <RefreshCw size={14} strokeWidth={1.5} /> Neu laden
        </button>
      </div>

      {/* Filter Chips */}
      {filter === "done" && filtered.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm" data-testid="bulk-archive-bar">
          <span className="text-emerald-800">{filtered.length} Kunden sind fertig (Zeitraum vorbei, alles abgerechnet, keine offenen Unterschriften).</span>
          <button onClick={async () => {
            if (!window.confirm(`Alle ${filtered.length} Kunden archivieren? Die Akten bleiben im Archiv erreichbar.`)) return;
            try { const r = await api.post("/clients/archive-bulk", { client_ids: filtered.map((c) => c.client_id) }); toast.success(`${r.data.archived} Kunden archiviert`); load(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
          }} className="ml-auto inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 text-xs transition-colors" data-testid="bulk-archive-btn">
            <Archive size={13} /> Alle {filtered.length} archivieren
          </button>
        </div>
      )}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="kunden-status-filters">
        {[
          { k: "all", l: `Alle · ${stats.total}` },
          { k: "low", l: `Wenig UE · ${stats.low}`, danger: stats.low > 0 },
          { k: "pending", l: `Wartet auf Kunde · ${stats.pending}`, warn: stats.pending > 0 },
          { k: "upcoming", l: `Kommt bald · ${stats.upcoming}` },
          { k: "running", l: `Läuft gerade · ${stats.running}` },
          { k: "billing", l: `Noch abrechnen · ${stats.billing}`, warn: stats.billing > 0 },
          { k: "docs", l: `Unterlagen fehlen · ${stats.docs}`, warn: stats.docs > 0 },
          { k: "ended", l: `Zeitraum abgelaufen · ${stats.ended}` },
          { k: "done", l: `Fertig für Ablage · ${stats.done}` },
          { k: "archived", l: `Ablage · ${archivedList ? archivedList.length : archivedCount}` },
        ].map(({ k, l, danger, warn }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`px-4 py-2 text-sm border transition-colors ${
              filter === k
                ? "bg-slate-900 text-white border-slate-900"
                : danger
                ? "border-red-300 text-red-700 hover:bg-red-50"
                : warn
                ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading && <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>}

      {!loading && filtered.length === 0 && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <p className="text-sm text-slate-600">Keine Kunden in dieser Ansicht.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200">
        {filtered.map((c, i) => {
          // "Kritisch" = Zeit wird eng (mehr als 12 UE/Woche nötig, um pünktlich fertig zu
          // werden) — NICHT einfach "wenig UE übrig". Ein Kunde, der schon alles verbraucht
          // hat oder noch genug Zeit übrig hat, ist nicht kritisch, egal wie wenig UE übrig ist.
          const isCritical = c.pace_status === "red";
          const usedUp = c.total_ue > 0 && c.remaining_ue <= 0;
          const isLowish = !isCritical && !usedUp && (c.pace_status === "orange" || (c.total_ue > 0 && c.remaining_ue <= 20));
          const endedSoon = c.end_date && c.end_date < today;
          return (
            <Link
              key={c.client_id}
              to={`/avgs/${c.client_id}`}
              data-testid={`row-${c.client_id}`}
              className={`grid grid-cols-12 items-center gap-4 px-5 py-4 border-slate-100 hover:bg-slate-50 transition-colors ${i > 0 ? "border-t" : ""}`}
            >
              <div className="col-span-4 min-w-0">
                <div className="flex items-center gap-2">
                  {isCritical && <AlertTriangle size={13} strokeWidth={2} className="text-red-600 flex-shrink-0" />}
                  <span className="text-sm font-medium text-slate-900 truncate">{c.name}</span>
                </div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">
                  Kd-Nr {c.customer_number || "—"} · {c.coach_names.join(", ") || "kein Coach"}
                </div>
              </div>

              <div className="col-span-4">
                {c.total_ue > 0 ? (
                  <>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className={`text-lg font-heading font-light tracking-tighter ${isCritical ? "text-red-700" : isLowish ? "text-amber-700" : "text-slate-900"}`} data-testid={`remaining-${c.client_id}`}>
                        {c.remaining_ue} UE
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {c.used_ue} / {c.total_ue} verbraucht
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 relative overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 ${isCritical ? "bg-red-600" : isLowish ? "bg-amber-500" : "bg-slate-900"}`}
                        style={{ width: `${Math.min(100, c.usage_pct)}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <span className="text-xs text-slate-400 italic">Kein UE-Kontingent gesetzt</span>
                )}
              </div>

              <div className="col-span-2 text-xs">
                <div className={`font-mono ${endedSoon ? "text-red-700" : "text-slate-600"}`}>
                  {formatDeShort(c.start_date)} – {formatDeShort(c.end_date)}
                </div>
                <div className="flex flex-wrap gap-x-3 mt-1 text-[10px] text-slate-500">
                  {c.upcoming_appointments > 0 && <span>{c.upcoming_appointments} anstehend</span>}
                  {c.pending_signatures > 0 && <span className="text-amber-700">{c.pending_signatures} offen</span>}
                  {c.unbilled_ue > 0 && <span className="text-amber-700" data-testid={`unbilled-${c.client_id}`}>{c.unbilled_ue} UE nicht abgerechnet</span>}
                  {c.docs_completeness < 100 && <span className="text-red-700">Unterlagen {Math.round(c.docs_completeness)} %</span>}
                </div>
              </div>

              <div className="col-span-2 flex items-center justify-end gap-2">
                {isCritical && <span className="text-[10px] font-medium bg-red-100 text-red-800 border border-red-200 px-2 py-1">Kritisch</span>}
                {isLowish && <span className="text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200 px-2 py-1 whitespace-nowrap">Bald leer</span>}
                {usedUp && <span className="text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 whitespace-nowrap">Aufgebraucht</span>}
                {c.archived
                  ? <button onClick={(e) => { e.preventDefault(); setArchived(c, false); }} className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] border border-slate-300 px-2 py-1 hover:border-slate-900" data-testid={`unarchive-${c.client_id}`}><ArchiveRestore size={12} /> Wiederherstellen</button>
                  : c.end_date && c.end_date < today && <button onClick={(e) => { e.preventDefault(); setArchived(c, true); }} className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] border border-slate-300 px-2 py-1 hover:border-slate-900" title="Kunde archivieren" data-testid={`archive-${c.client_id}`}><Archive size={12} /> Archivieren</button>}
                <ArrowRight size={14} strokeWidth={1.5} className="text-slate-400 flex-shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
